import * as React from "react";
import { select, scaleOrdinal, transition, scaleBand, scaleLinear, axisBottom, axisLeft, axisRight, max } from "d3";
import { Button } from "../../button/button.mjs";
import { ChartLegend } from "../chart-legend/chart-legend.mjs";
import debounce from "debounce";
import "tabbable";
import { Tag } from "../../tag/tag.mjs";
import { Tooltip } from "../../tooltip/tooltip.mjs";
const _ChartBar = class _ChartBar extends React.Component {
  constructor(props) {
    super(props);
    this.yMax = 0;
    this.y2Max = 0;
    this.containerWidth = 0;
    this.graphWidth = 0;
    this.graphHeight = 0;
    this.scrollableIndex = 0;
    this.scrollableLength = 0;
    this.ui = {
      groupMinWidth: 200,
      labelHeight: 0,
      paddingBottom: 30,
      paddingTop: 20,
      radius: 3,
      shadowColor: "var(--color-neutral-bg-subtle)",
      slot: 12,
      space: 3,
      width: 6
    };
    this.mounted = false;
    this.resize = () => {
      if (this.mounted) {
        this.updateGraph(false);
      }
    };
    this.wrapText = (text, width) => {
      const self = this;
      self.ui.labelHeight = 0;
      function setMaxLabelHeight(item) {
        self.ui.labelHeight = Math.max(self.ui.labelHeight, item.getBoundingClientRect().height - 20);
      }
      text.each(function() {
        const item = select(this);
        const words = item.text().split(/\s+/);
        if (words.length < 2 || self.props.rotateXAxisLabels) {
          setMaxLabelHeight(item.node());
          return item;
        }
        let word;
        let line = [];
        let lineNumber = 0;
        const lineHeight = 1.4;
        const y = item.attr("y");
        const dy = parseFloat(item.attr("dy"));
        let tspan = item.text(null).append("tspan").attr("x", 0).attr("y", y).attr("dy", dy + "em");
        for (word of words) {
          line.push(word);
          tspan.text(line.join(" "));
          const node = tspan.node();
          if (node && typeof node.getComputedTextLength === "function" && node.getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = item.append("tspan").attr("x", 0).attr("y", y).attr("dy", "".concat(++lineNumber * lineHeight + dy, "em")).text(word);
          }
        }
        setMaxLabelHeight(item.node());
      });
    };
    this.scrollPrev = () => {
      this.scrollableIndex -= 1;
      this.updateGraph(false);
    };
    this.scrollNext = () => {
      this.scrollableIndex += 1;
      this.updateGraph(false);
    };
    this.state = {
      scrollable: false,
      stackRefs: [],
      stackLayerRefs: []
    };
    this.debouncedResizeHandler = debounce(this.resize.bind(this), 100);
  }
  static compareData(o1, o2) {
    if (typeof o1 === "undefined" || o1 === null || typeof o2 === "undefined" || o2 === null) {
      return o1 === o2;
    }
    return typeof o1 === "object" && o1 !== null && typeof o2 === "object" && o2 !== null && Object.keys(o1).length > 0 ? Object.keys(o1).length === Object.keys(o2).length && Object.keys(o1).every((p) => _ChartBar.compareData(o1[p], o2[p])) : o1 === o2;
  }
  componentDidMount() {
    this.mounted = true;
    this.colors = scaleOrdinal(this.props.colors);
    this.createGraph();
    window.addEventListener("resize", this.debouncedResizeHandler);
  }
  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener("resize", this.debouncedResizeHandler);
    this.debouncedResizeHandler.clear();
  }
  componentDidUpdate(prevProps) {
    if (!_ChartBar.compareData(prevProps.data, this.props.data) && this.mounted) {
      this.scrollableIndex = 0;
      this.updateGraph(false);
    }
  }
  BEM() {
    const classArray = ["chart-bar"];
    if (this.props.legend) {
      classArray.push("chart-bar--legend");
      classArray.push("chart-bar--legend-".concat(this.props.legend));
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    return classArray.join(" ");
  }
  get hasNoData() {
    return this.props.data.json.length === 0;
  }
  /* Chart */
  createGraph() {
    this.transition = transition().duration(800).ease(this.easeOutQuint);
    this.svgEl = select(this.containerEl).append("svg").attr("class", "chart-bar__svg");
    this.graphEl = this.svgEl.append("g").attr("class", "chart-bar__graph");
    this.axisEl = this.graphEl.append("g").attr("class", "chart-bar__axis");
    this.axisYEl = this.axisEl.append("g").attr("class", "chart-bar__axis-y");
    this.axisY2El = this.axisEl.append("g").attr("class", "chart-bar__axis-y2");
    this.axisYGridEl = this.axisEl.append("g").attr("class", "chart-bar__axis-y-grid");
    this.axisXEl = this.axisEl.append("g").attr("class", "chart-bar__axis-x");
    this.updateGraph(true);
  }
  updateGraph(firstLoad) {
    const graphData = this.getGraphData();
    this.containerWidth = this.containerEl.clientWidth;
    this.yMax = this.getGraphYMax(graphData);
    this.y2Max = this.getGraphY2Max(graphData);
    const paddingLeft = this.getGraphPadding(this.yMax);
    const paddingRight = this.getGraphPadding(this.y2Max);
    this.setGraphSizeAndScale(graphData, paddingLeft, paddingRight);
    this.updateGraphAxis();
    this.updateGraphGroups(this.getVisibleData(graphData), firstLoad);
    const paddingLeftNext = this.getGraphPadding(this.getLongestTick(this.axisYEl));
    const paddingRightNext = this.getGraphPadding(this.getLongestTick(this.axisY2El));
    if (paddingLeft !== paddingLeftNext || paddingRight !== paddingRightNext) {
      this.setGraphSizeAndScale(graphData, paddingLeftNext, paddingRightNext);
      this.updateGraphAxisX();
      this.updateGraphGroups(this.getVisibleData(graphData), firstLoad);
    }
    this.svgEl.attr("width", this.containerWidth).attr("height", this.props.height + this.ui.labelHeight);
  }
  setGraphSizeAndScale(graphData, paddingLeft, paddingRight) {
    const lastTickWidth = this.getLastTickWidth(this.axisXEl);
    this.graphWidth = this.containerWidth - paddingLeft - paddingRight - lastTickWidth / 3;
    this.graphHeight = this.props.height - this.ui.paddingTop - this.ui.paddingBottom;
    const visibleData = this.getVisibleData(graphData);
    this.graphEl.attr("transform", "translate(" + paddingLeft + ", " + this.ui.paddingTop + ")");
    this.axisY2El.attr("transform", "translate(" + this.graphWidth + ", 0)");
    this.axisXEl.attr("transform", "translate(0, " + this.graphHeight + ")");
    this.xScale = scaleBand().rangeRound([0, this.graphWidth]).domain(visibleData.map((d) => d.original.key));
    this.yScale = scaleLinear().rangeRound([this.graphHeight, 0]).domain([0, this.yMax]);
    this.y2Scale = scaleLinear().rangeRound([this.graphHeight, 0]).domain([0, this.y2Max]);
  }
  getLastTickWidth(axis) {
    let tickWidth = 0;
    if (axis) {
      const element = axis.selectAll(".tick:last-of-type").node();
      tickWidth = element && element.getBoundingClientRect().width;
    }
    return tickWidth;
  }
  getLongestTick(axis) {
    let longestEl = 0;
    if (axis) {
      axis.selectAll(".tick text").each((el) => {
        if (("" + el).length > ("" + longestEl).length || longestEl === 0 && el !== 0) {
          longestEl = el;
        }
      });
    }
    return longestEl;
  }
  updateGraphAxis() {
    this.svgEl.selectAll("*").interrupt();
    this.updateGraphAxisX();
    this.updateGraphAxisY();
    this.updateGraphAxisY2();
    this.updateGraphAxisYGrid();
  }
  getCustomTicks(format) {
    const graphData = this.getGraphData();
    const visibleTicks = this.getVisibleData(graphData).map((data) => data.original.key);
    if (format) {
      return format(visibleTicks);
    }
    return visibleTicks;
  }
  updateGraphAxisX() {
    const self = this;
    const xAxisCall = axisBottom(this.xScale).tickValues(this.getCustomTicks(this.props.format.xCustomTicks)).tickFormat((xKey) => this.getFormattedXKey(xKey.toString()));
    function wrapText(text, width) {
      self.wrapText(text, width);
    }
    if (this.props.rotateXAxisLabels) {
      this.axisXEl.call(xAxisCall).selectAll(".tick text").style("text-anchor", "end").attr("dx", "-5px").attr("dy", "-5px").attr("transform", "rotate(-90)").call(wrapText, this.xScale.bandwidth());
    } else {
      this.axisXEl.call(xAxisCall).selectAll(".tick text").call(wrapText, this.xScale.bandwidth());
    }
  }
  updateGraphAxisY() {
    const yAxisCall = axisLeft(this.yScale).tickFormat((yVal) => this.getFormattedYVal(+yVal)).ticks(this.props.ticks);
    this.axisYEl.transition().call(yAxisCall);
  }
  updateGraphAxisY2() {
    if (!this.props.data.y2) {
      return;
    }
    const y2AxisCall = axisRight(this.y2Scale).tickFormat((yVal) => this.getFormattedYVal(yVal)).ticks(this.props.ticks);
    this.axisY2El.transition().attr("transform", "translate(" + this.graphWidth + ", 0)").call(y2AxisCall);
  }
  updateGraphAxisYGrid() {
    const yAxisGridCall = axisLeft(this.yScale).tickSize(-this.graphWidth).tickFormat(() => "").ticks(this.props.ticks);
    this.axisYGridEl.transition().call(yAxisGridCall);
  }
  updateGraphGroups(groupsData, firstLoad) {
    const self = this;
    const graphGroups = this.graphEl.selectAll(".chart-bar__group").data(
      groupsData,
      (data) => data.original.key
    );
    graphGroups.exit().attr("opacity", "1").transition().attr("opacity", "0").remove();
    function graphGroupPosX(d) {
      const posX = self.xScale(d.original.key) + self.xScale.bandwidth() * 0.5 - d.bars.length * self.ui.slot * 0.5;
      return "translate(".concat(posX, ",0)");
    }
    function updateGraphBars(data) {
      const barsCount = groupsData.reduce((prevValue, currentValue) => prevValue + currentValue.bars.length, 0);
      self.updateGraphBars(this, data, firstLoad, barsCount);
    }
    graphGroups.transition().attr("transform", graphGroupPosX).each(updateGraphBars);
    graphGroups.enter().append("g").attr("class", "chart-bar__group").attr("transform", (d) => graphGroupPosX(d)).each(updateGraphBars);
    const stackRefs = [];
    const stackLayerRefs = [];
    this.graphEl.selectAll(".chart-bar__group").each(function mapGroups(data) {
      const xKey = data.original.key;
      const xData = data.original;
      select(this).selectAll(".chart-bar__bar").each(function mapStacks(data2) {
        const yKey = data2.key;
        stackRefs.push({
          ref: select(this).node(),
          xKey,
          xData,
          yKey
        });
      });
      select(this).selectAll(".chart-bar__stack-layer").each(function mapGroups2() {
        stackLayerRefs.push({
          ref: select(this).node(),
          xKey,
          xData,
          yKey: null
        });
      });
    });
    self.setState({
      stackRefs,
      stackLayerRefs
    });
  }
  updateGraphBars(ref, groupData, firstLoad, totalBars) {
    this.updateGraphShadowBar(ref, firstLoad, groupData, totalBars);
    this.updateGraphStack(ref, groupData);
    this.updateGraphStackClip(ref, firstLoad, groupData, totalBars);
    if (this.props.labels) {
      this.updateGraphStackLabel(ref, firstLoad, groupData, totalBars);
    }
    this.updateGraphGroupedTooltipLayer(ref, groupData);
  }
  getAnimationDelay(barsCount) {
    const maximumAnimationLength = 800;
    return Math.ceil(maximumAnimationLength / barsCount);
  }
  updateGraphShadowBar(ref, firstLoad, groupData, totalBars) {
    const self = this;
    const y = groupData.cat === "y" ? self.yScale : self.y2Scale;
    const height = groupData.cat === "y" ? this.yMax : this.y2Max;
    const animationDelay = self.getAnimationDelay(totalBars);
    function getPath(d, {}, {}, start = false) {
      const pathX = self.ui.space;
      const pathY = Number(start) || (!d ? y(0) : y(d.yEnd)) || 0;
      const pathW = self.ui.width;
      const pathH = Number(start) || (!d ? 0 : y(height - d.yEnd)) || 0;
      return self.getRoundedRect(pathX, pathY, pathW, pathH);
    }
    const graphShadowBar = select(ref).selectAll(".chart-bar__shadow").data((d) => d.shadows);
    graphShadowBar.exit().remove();
    graphShadowBar.select("path").transition().attr("transform", ({}, index) => "translate(" + index * self.ui.slot + ",0)").attr("d", getPath);
    graphShadowBar.enter().append("path").attr("class", "chart-bar__shadow").attr("transform", ({}, index) => "translate(" + index * self.ui.slot + ",0)").attr("fill", ({}) => self.ui.shadowColor).attr("d", (d) => getPath(d, 0, [], true)).transition().delay(({}) => firstLoad ? animationDelay * 0 : 0).attr("d", getPath);
  }
  updateGraphStack(ref, groupData) {
    const self = this;
    const graphStack = select(ref).selectAll(".chart-bar__stack").data(
      (d) => d.bars,
      (d) => d.id
    );
    function updateGraphStackBar() {
      self.updateGraphStackBar(this, groupData);
    }
    graphStack.exit().remove();
    graphStack.transition().attr("transform", ({}, index) => "translate(" + index * self.ui.slot + ",0)").each(updateGraphStackBar);
    graphStack.enter().append("g").attr("class", "chart-bar__stack").attr("clip-path", (d) => "url(#" + d.id + ")").attr("transform", ({}, index) => "translate(" + index * self.ui.slot + ",0)").each(updateGraphStackBar);
  }
  updateGraphGroupedTooltipLayer(ref, groupData) {
    const self = this;
    const y = groupData.cat === "y" ? self.yScale : self.y2Scale;
    const height = groupData.cat === "y" ? this.yMax : this.y2Max;
    const graphStackLayer = select(ref).selectAll(".chart-bar__stack-layer").data(
      (d) => d.bars,
      (d) => d.id
    );
    function getPath(d, index) {
      let lastItem = d.stack[d.stack.length - 1];
      if (groupData.shadows.length && groupData.shadows[index]) {
        lastItem = groupData.shadows[index];
      }
      const pathX = self.ui.space;
      const pathY = y(lastItem.yEnd) || 0;
      const pathW = self.ui.width;
      const pathH = y(height - lastItem.yEnd) || 0;
      return self.getRoundedRect(pathX, pathY, pathW, pathH);
    }
    graphStackLayer.exit().remove();
    if (self.props.tooltip && self.props.tooltip.grouped) {
      graphStackLayer.select("path").attr("d", getPath);
      graphStackLayer.enter().append("path").attr("class", (d) => "chart-bar__stack-layer chart-bar__stack-layer--" + d.id).attr("d", getPath).style("fill", "transparent");
    }
  }
  updateGraphStackClip(ref, firstLoad, groupData, totalBars) {
    const self = this;
    const y = groupData.cat === "y" ? self.yScale : self.y2Scale;
    const height = groupData.cat === "y" ? this.yMax : this.y2Max;
    const animationDelay = self.getAnimationDelay(totalBars);
    const graphStackClip = select(ref).selectAll(".chart-bar__stack-clip").data(
      (d) => d.bars,
      (d) => d.id
    );
    function getPath(d, {}, {}, start = false) {
      const lastItem = d.stack[d.stack.length - 1];
      const pathX = self.ui.space;
      const pathY = (start ? y(0) : y(lastItem.yEnd)) || 0;
      const pathW = self.ui.width;
      const pathH = (start ? 0 : y(height - lastItem.yEnd)) || 0;
      return self.getRoundedRect(pathX, pathY, pathW, pathH);
    }
    graphStackClip.exit().remove();
    graphStackClip.select("path").transition().attr("d", getPath);
    graphStackClip.enter().append("clipPath").attr("class", "chart-bar__stack-clip").attr("id", (d) => d.id).append("path").attr("d", (d) => getPath(d, 0, [], true)).transition().delay((d) => firstLoad ? animationDelay * d.count : 0).attr("d", getPath);
  }
  updateGraphStackLabel(ref, firstLoad, groupData, totalBars) {
    const self = this;
    const y = groupData.cat === "y" ? self.yScale : self.y2Scale;
    const animationDelay = self.getAnimationDelay(totalBars);
    const graphLabel = select(ref).selectAll(".chart-bar__stack-label").data(
      (d) => self.props.labels ? d.bars : [],
      (d) => d.id
    );
    function graphLabelXBefore({}, index) {
      const pathY = (y(0) || 0) - 5;
      return "translate(" + (index * self.ui.slot + self.ui.slot * 0.5) + "," + (pathY - 5) + ")";
    }
    function graphLabelX(d, index) {
      const lastItem = d.stack[d.stack.length - 1];
      const pathY = y(lastItem.yEnd) || 0;
      return "translate(" + (index * self.ui.slot + self.ui.slot * 0.5) + "," + (pathY - 5) + ")";
    }
    graphLabel.exit().remove();
    graphLabel.transition().delay((d) => firstLoad ? animationDelay * d.count : 0).text((d) => self.getFormattedYVal(d.stack[d.stack.length - 1].yEnd)).attr("transform", graphLabelX);
    graphLabel.enter().append("text").text((d) => self.getFormattedYVal(d.stack[d.stack.length - 1].yEnd)).attr("class", "chart-bar__stack-label").attr("transform", graphLabelXBefore).transition().delay((d) => firstLoad ? animationDelay * d.count : 0).attr("transform", graphLabelX);
  }
  updateGraphStackBar(ref, groupData) {
    const self = this;
    const y = groupData.cat === "y" ? self.yScale : self.y2Scale;
    const graphBar = select(ref).selectAll("rect").data((d) => d.stack);
    graphBar.exit().transition().attr("height", 0).remove();
    graphBar.transition().attr("width", self.ui.slot).attr("y", (d) => y(d.yEnd) || 0).attr("fill", (d) => self.colors(d.key)).attr("height", (d) => (y(d.yBegin) || 0) - (y(d.yEnd) || 0));
    graphBar.enter().append("rect").attr("class", (d) => "chart-bar__bar chart-bar__bar--" + d.key).attr("width", self.ui.slot).attr("y", (d) => y(d.yEnd) || 0).attr("height", (d) => (y(d.yBegin) || 0) - (y(d.yEnd) || 0)).attr("fill", (d) => self.colors(d.key));
  }
  getGraphData() {
    const { json: data, groups, shadow = [] } = this.props.data;
    const shadowKeys = shadow.filter((item) => item);
    const yKeys = [].concat(...groups);
    let barCounter = 0;
    const items = data.map((item, itemIndex) => {
      const group = {
        bars: [],
        shadows: [],
        max: 0,
        original: item,
        cat: "y"
      };
      const bars = [];
      const shadows = [];
      const posY = [];
      if (this.props.data.y2) {
        if (this.props.data.y2.indexOf(item.key) !== -1) {
          group.cat = "y2";
        }
      }
      if (shadow.length) {
        for (const key of shadowKeys) {
          if (item[key]) {
            for (let i = 0; i < shadow.length; i += 1) {
              if (shadow[i] === key) {
                shadows[i] = {
                  key,
                  yBegin: 0,
                  yEnd: +item[key]
                };
              }
            }
          }
        }
        group.shadows = shadows;
      }
      for (const key of yKeys) {
        if (item[key]) {
          for (let i = 0; i < groups.length; i += 1) {
            if (groups[i].indexOf(key) !== -1) {
              let yBegin = 0;
              let yEnd = 0;
              if (!bars[i]) {
                bars[i] = {
                  count: barCounter,
                  id: this.props.id + "-" + itemIndex + "-" + i,
                  stack: [],
                  total: 0
                };
                barCounter++;
              }
              if (!posY[i]) {
                posY[i] = 0;
              }
              yBegin = posY[i];
              posY[i] += +item[key];
              yEnd = posY[i];
              bars[i].total = yEnd;
              bars[i].stack.push({ key, yBegin, yEnd });
            }
          }
        }
      }
      group.bars = bars.filter((stack) => stack);
      const barsMax = max(group.bars, ({ total }) => total) || 0;
      const shadowMax = max(group.shadows, (shadowItem) => shadowItem ? shadowItem.yEnd : 0) || 0;
      group.max = Math.max(barsMax, shadowMax);
      return group;
    });
    return items;
  }
  getVisibleData(data) {
    if (!this.props.scrollable) {
      this.setState({
        scrollable: false
      });
      return data;
    }
    const groupMinWidth = this.props.scrollable.groupMinWidth || 100;
    const scrollableLength = data.length;
    const scrollableLengthVisible = Math.floor(this.graphWidth / groupMinWidth);
    const scrollableWidth = scrollableLength * groupMinWidth;
    this.scrollableLength = Math.max(0, scrollableLength - scrollableLengthVisible);
    if (this.scrollableIndex > scrollableLength - scrollableLengthVisible) {
      this.scrollableIndex = this.scrollableLength;
    }
    if (this.scrollableIndex < 0) {
      this.scrollableIndex = 0;
    }
    if (scrollableWidth > this.graphWidth) {
      if (!this.state.scrollable) {
        this.scrollableIndex = this.scrollableLength;
      }
      this.setState({
        scrollable: true
      });
      return data.slice(this.scrollableIndex, this.scrollableIndex + scrollableLengthVisible);
    } else {
      this.setState({
        scrollable: false
      });
    }
    return data;
  }
  getGraphYMax(groupsData) {
    if (this.hasNoData) {
      return 10;
    }
    let data = groupsData;
    const y2 = this.props.data.y2;
    if (y2) {
      data = groupsData.filter((item) => y2.indexOf(item.original.key) === -1);
    }
    return this.getGraphCeilNumber(max(data, (d) => +d.max) || 0);
  }
  getGraphY2Max(groupsData) {
    const y2 = this.props.data.y2;
    if (y2) {
      const data = groupsData.filter((item) => y2.indexOf(item.original.key) !== -1);
      return this.getGraphCeilNumber(max(data, (d) => +d.max) || 0);
    }
    return 0;
  }
  /**
   * Return rounded up number to give space to top of chart.
   * Examples as maxValue => returnValue
   * 0,62 => 0.7 | 4.5 => 5 | 6 => 6 | 11 => 15 | 125 => 125 | 126 => 150 etc.
   */
  getGraphCeilNumber(val) {
    let roundedValue = val;
    const isLowerThanOne = val < 1;
    if (isLowerThanOne) {
      roundedValue = Math.ceil(100 * val);
    } else if (!Number.isInteger(val)) {
      roundedValue = Math.ceil(val);
    }
    const digits = roundedValue.toString().length - 1;
    const roundBy = Math.pow(5, digits);
    const ceilNumber = Math.ceil(roundedValue / roundBy) * roundBy;
    if (isLowerThanOne) {
      return ceilNumber / 100;
    }
    return ceilNumber;
  }
  getGraphPadding(val) {
    if (val === 0) {
      return 0;
    }
    const formattedVal = this.getFormattedYVal(val);
    const formattedValLength = formattedVal.length;
    return formattedValLength * 8 + 10;
  }
  getRoundedRect(x, y, w, h) {
    const rTop = h >= this.ui.radius * 2 ? this.ui.radius : h / 2;
    const rBottom = h >= this.ui.radius * 2 ? this.ui.radius : h / 2;
    const path = "M ".concat(x + rTop, " ").concat(y, "h ").concat(w - rTop * 2, "a ").concat(rTop, ",").concat(rTop, " 0 0 1 ").concat(rTop, ",").concat(rTop, "v ").concat(h - rTop - rBottom, "a ").concat(rBottom, ",").concat(rBottom, " 0 0 1 -").concat(rBottom, ",").concat(rBottom, "h ").concat(-(w - rBottom * 2), "a ").concat(rBottom, ",").concat(rBottom, " 0 0 1 -").concat(rBottom, ",-").concat(rBottom, "v ").concat(-(h - rTop - rBottom), "a ").concat(rTop, ",").concat(rTop, " 0 0 1 ").concat(rTop, ",-").concat(rTop, "z");
    return path;
  }
  /* Title */
  getTitle() {
    return /* @__PURE__ */ React.createElement("div", { className: "chart-bar__title" }, /* @__PURE__ */ React.createElement("h3", { className: "chart-bar__title-text text-tiny text-color-normal" }, this.props.title));
  }
  /* Legend */
  getLegend() {
    let shadowItems = [];
    if (this.props.data.shadow) {
      const shadowKeys = this.props.data.shadow.filter((item) => item);
      shadowItems = shadowKeys.map((key) => {
        return {
          color: this.ui.shadowColor,
          key,
          text: this.getFormattedYKey(key)
        };
      });
    }
    const groupItems = [].concat(...this.props.data.groups).map((key) => {
      return {
        color: this.colors(key),
        key,
        text: this.getFormattedYKey(key)
      };
    });
    const items = [...shadowItems, ...groupItems];
    return /* @__PURE__ */ React.createElement(ChartLegend, { items, format: this.props.format.legendYKey, className: "chart-bar__legend" });
  }
  /* Scroll Navigation */
  getScrollNavigation() {
    if (this.props.scrollable) {
      if (this.props.scrollable.autoHide && this.scrollableIndex === 0 && this.scrollableIndex === this.scrollableLength) {
        return null;
      }
      return /* @__PURE__ */ React.createElement("ul", { className: "chart-bar__scrollable" }, /* @__PURE__ */ React.createElement("li", { className: "chart-bar__scrollable-item" }, /* @__PURE__ */ React.createElement(
        Button,
        {
          text: this.props.scrollable.prevText,
          disabled: this.scrollableIndex === 0,
          icon: "#arrow-left",
          size: "small",
          variant: "secondary",
          onClick: this.scrollPrev
        }
      )), /* @__PURE__ */ React.createElement("li", { className: "chart-bar__scrollable-item" }, /* @__PURE__ */ React.createElement(
        Button,
        {
          text: this.props.scrollable.nextText,
          disabled: this.scrollableIndex === this.scrollableLength,
          icon: "#arrow-right",
          size: "small",
          variant: "secondary",
          onClick: this.scrollNext
        }
      )));
    }
    return null;
  }
  /* Tooltip */
  getTooltip() {
    if (!this.props.tooltip) {
      return null;
    }
    if (!this.props.tooltip.show) {
      return null;
    }
    const tooltipProps = this.getTooltipSettings();
    if (!this.props.tooltip.grouped) {
      const refs2 = this.state.stackRefs;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, refs2.map((ref, index) => {
        if (!ref) {
          return;
        }
        return /* @__PURE__ */ React.createElement(Tooltip, { key: index, ...tooltipProps, referenceElement: ref.ref }, this.getTooltipTitle(ref.xKey), this.props.tooltip && this.getTooltipItems(ref.xData, ref.yKey, this.props.tooltip));
      }));
    }
    const refs = this.state.stackLayerRefs;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, refs.map((ref, index) => {
      if (!ref) {
        return;
      }
      return /* @__PURE__ */ React.createElement(Tooltip, { key: index, ...tooltipProps, referenceElement: ref.ref }, this.getTooltipTitle(ref.xKey), this.props.tooltip && this.getTooltipItems(ref.xData, ref.yKey, this.props.tooltip));
    }));
  }
  getTooltipTitle(xKey) {
    return /* @__PURE__ */ React.createElement("h4", { className: "chart__tooltip-title" }, this.props.format.tooltipKey ? this.getFormattedTooltipKey(xKey) : this.getFormattedXKey(xKey));
  }
  getTooltipItems(xData, yKey, config) {
    if (config.grouped) {
      const yKeys = Object.keys(xData).filter((key) => key !== "key");
      return /* @__PURE__ */ React.createElement("ul", { className: "chart__tooltip-list" }, yKeys.map((key) => this.getTooltipItem(key, xData[key])));
    } else {
      return /* @__PURE__ */ React.createElement("ul", { className: "chart__tooltip-list" }, this.getTooltipItem(yKey, xData[yKey]));
    }
  }
  getTooltipItem(yKey, yVal) {
    const yKeyFormatted = this.getFormattedYKey(yKey);
    const yValFormatted = this.getFormattedYVal(yVal);
    const isShadowKey = this.props.data.shadow ? this.props.data.shadow.indexOf(yKey) !== -1 : false;
    const yKeyColor = isShadowKey ? this.ui.shadowColor : this.colors(yKey);
    const getYValAndYKey = () => {
      var _a;
      if ((_a = this.props.tooltip) == null ? void 0 : _a.formatKeyAndVal) {
        return /* @__PURE__ */ React.createElement("span", null, yKeyFormatted, ": ", /* @__PURE__ */ React.createElement("strong", { className: "chart__tooltip-yVal" }, yValFormatted));
      }
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("strong", { className: "chart__tooltip-yVal" }, yValFormatted), " ", /* @__PURE__ */ React.createElement("span", null, yKeyFormatted));
    };
    return /* @__PURE__ */ React.createElement("li", { key: yKey, className: "chart__tooltip-list-item" }, /* @__PURE__ */ React.createElement("span", { className: "chart__tooltip-color", style: { background: yKeyColor } }), getYValAndYKey());
  }
  getTooltipSettings() {
    return {
      placement: "top"
    };
  }
  /* Tag */
  renderTag() {
    return /* @__PURE__ */ React.createElement(Tag, { className: "chart-bar__tag", size: "tiny", text: this.props.noDataText });
  }
  /* Format */
  getFormattedYKey(yKey) {
    return this.getFormattedVal(this.props.format.yKey, yKey);
  }
  getFormattedXKey(xKey) {
    return this.getFormattedVal(this.props.format.xKey, xKey);
  }
  getFormattedTooltipKey(tooltipKey) {
    return this.getFormattedVal(this.props.format.tooltipKey, tooltipKey);
  }
  getFormattedYVal(yVal) {
    return this.getFormattedVal(this.props.format.yVal, yVal);
  }
  getFormattedVal(format, val) {
    if (this.hasNoData && val !== 0) {
      if (typeof format === "function" && !(format instanceof Array)) {
        return new Array(format(val).length).join(" ");
      }
      return " ";
    }
    if (format) {
      if (typeof format === "object" && !(format instanceof Array)) {
        return format[val];
      }
      if (typeof format === "function" && !(format instanceof Array)) {
        return format(val);
      }
      return "".concat(val, " ").concat(format);
    }
    return val.toString();
  }
  /* Transitions */
  easeOutQuint(t) {
    return 1 + --t * t * t * t * t;
  }
  /* Render */
  render() {
    return /* @__PURE__ */ React.createElement("div", { className: this.BEM() }, this.props.title && this.getTitle(), /* @__PURE__ */ React.createElement("div", { className: "chart-bar__inner" }, /* @__PURE__ */ React.createElement("div", { className: "chart-bar__graph-container", ref: (containerEl) => this.containerEl = containerEl }), this.hasNoData && this.renderTag()), this.colors && this.props.legend && !this.hasNoData && this.getLegend(), this.props.scrollable && this.getScrollNavigation(), this.getTooltip());
  }
};
_ChartBar.defaultProps = {
  colors: [
    "var(--color-chart-1)",
    "var(--color-chart-2)",
    "var(--color-chart-3)",
    "var(--color-chart-4)",
    "var(--color-chart-5)",
    "var(--color-chart-6)",
    "var(--color-chart-7)"
  ],
  format: {
    legendYKey: void 0,
    xKey: void 0,
    yKey: void 0,
    yVal: void 0
  },
  height: 260,
  ticks: 3
};
let ChartBar = _ChartBar;
export {
  ChartBar
};
